import { auth, firebase } from '@/firebase'
import * as firebaseui from 'firebaseui'

import { Subject } from 'rxjs'

import clone from 'lodash/clone'
import assign from 'lodash/assign'

import store from '@/store'
import { users } from '@/database'
import { createSessionToken } from '@/database/auth'

import { sharedInstance as analytics } from './AnalyticsService'

import { sharedInstance as loading } from '@/signals/LoadingSignal'

export default class AuthService
{
	private signedIn = false
	private authUI: firebaseui.auth.AuthUI
	private defaultAuthUIConfig: firebaseui.auth.Config

	private onSignedInSubject = new Subject<UserData>()
	private onSignedOutSubject = new Subject()
	private userDataListener?: () => void

	constructor()
	{
		this.authUI = new firebaseui.auth.AuthUI(auth)

		const self = this
		this.defaultAuthUIConfig = {
			signInSuccessUrl: '',
			signInOptions: [
				firebase.auth.EmailAuthProvider.PROVIDER_ID,
				firebase.auth.GoogleAuthProvider.PROVIDER_ID,
				firebase.auth.GithubAuthProvider.PROVIDER_ID
			],
			tosUrl: () => {
				console.log('tos')
			},
			privacyPolicyUrl: () => {
				console.log('privacy policy')
			},
			autoUpgradeAnonymousUsers: true,
			credentialHelper: firebaseui.auth.CredentialHelper.NONE,
			callbacks: {
				signInSuccessWithAuthResult: function (authResult, redirectURL) {
					const user = auth.currentUser!
					self.handleUserSignedIn(user)

					const isNewUser = authResult.additionalUserInfo.isNewUser
					const providerId = authResult.additionalUserInfo.providerId
					if (isNewUser)
					{
						analytics.logSignUp(providerId)
					}
					else
					{
						analytics.logLogin(providerId)
					}

					analytics.setUserId(user.uid)

					return false
				},
				signInFailure: function (error) {
					// TODO: handle this
					console.error(error)
					return Promise.reject(error)
				}
			}
		}

		auth.onAuthStateChanged(user => {
			if (!user)
			{
				// signed out or not logged in
				this.setSignedIn(false)
				return
			}

			this.handleUserSignedIn(user)
		})
	}

	get isSignedIn()
	{
		return this.signedIn
	}

	get user()
	{
		if (auth.currentUser)
		{
			return auth.currentUser
		}

		return null
	}

	async signOut()
	{
		await auth.signOut()

		if (this.userDataListener)
		{
			this.userDataListener()
			this.userDataListener = undefined
		}

		this.onSignedOutSubject.next()
	}

	onSignedInAsObservable()
	{
		return this.onSignedInSubject.asObservable()
	}

	onSignedOutAsObservable()
	{
		return this.onSignedOutSubject.asObservable()
	}

	signInWithUI(containerId: string | undefined = undefined)
	{
		if (!containerId)
		{
			containerId = '#firebaseui-auth-container'
		}

		const config = clone(this.defaultAuthUIConfig)

		config.signInSuccessUrl = window.location.href
		this.authUI.start(containerId, config)
	}

	private setSignedIn(v: boolean)
	{
		this.signedIn = v
		store.commit('auth/setSignedIn', v)
	}

	private async handleUserSignedIn(user: firebase.User | null)
	{
		if (!user)
		{
			return
		}

		loading.fire({ active: true })

		const results = await Promise.all([
			createSessionToken(store.getters['auth/isProRequest']),
			user.getIdTokenResult()
		])

		const sessionToken = results[0]
		if (sessionToken)
		{
			store.commit('auth/setSessionToken', sessionToken)
		}

		let data = {
			displayName: user.displayName,
			email: user.email,
			emailVerified: user.emailVerified,
			phoneNumber: user.phoneNumber,
			photoURL: user.photoURL,
			uid: user.uid,
			isAnonymous: user.isAnonymous
		}

		const tokenResult = results[1]
		if (tokenResult.claims.admin)
		{
			store.commit('auth/setAdmin', true)
		}

		// @ts-ignore
		if (window.FS)
		{
			// @ts-ignore
			// eslint-disable-next-line
			FS.identify(data.email, data)
		}

		const ref = users.doc(user.uid)

		this.userDataListener = ref.onSnapshot(snap => {
			const dbData = snap.exists ? snap.data() : {}

			assign(data, dbData)

			store.commit('auth/setUser', clone(data))

			if (!this.signedIn)
			{
				this.setSignedIn(true)

				this.onSignedInSubject.next(data)

				loading.fire({ active: false })
			}
		})
	}
}

const sharedInstance = new AuthService()

export {
	sharedInstance
}
